import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode } from 'react';

import { AppButton } from '@/components/atoms';
import { type CompanyStoryCta } from '@/hooks/useCompanyStoryBand';
import { displaySize } from '@/theme';

/**
 * The heading's size on a phone, and why it is not simply `h2`.
 *
 * Both numbers are `SectionHeading`'s, which is the point: every other band
 * heading on the site steps down to the scale's `h5` rung on a phone, for the
 * reason that component sets out at length — `displaySize.h2` bottoms out at
 * 31px, and 31px over a narrow measure sets a heading on four lines and fills
 * a screenful with type before any content. This band's heading does the same
 * job in the same place, so it takes the same treatment rather than inventing
 * a third answer.
 *
 * It does mean the About page's story heading now matches the rest of that
 * page's headings on a phone, where before this band was the one that did not.
 */
const TITLE_SIZE = { xs: '1.25rem', md: displaySize.h2 };

type Props = {
  /** Target of the enclosing Section's `aria-labelledby`. */
  titleId: string;
  eyebrow: string;
  title: string;
  lede: string;
  cta: CompanyStoryCta | null;
  /**
   * The narrative, for a density that sets it under the heading rather than in
   * a column beside it. It goes between the lede and the button, so the button
   * stays the last thing in the block at either density.
   */
  children?: ReactNode;
};

/**
 * The company story band's heading block: label, title, lede and the link out.
 *
 * Shared by both densities because it is the one part of the band they agree
 * on — what differs between them is where this block sits and what, if
 * anything, sits inside it. The full band puts it in a sticky rail with the
 * narrative in the opposite column; the compact band puts the narrative inside
 * it and the facts opposite.
 *
 * It renders no `Reveal` of its own. The full band's copy is inside a sticky
 * container, and a reveal wrapper there would give the column nothing to stick
 * within (see `CompanyStoryFull`), so the host decides where the animation
 * boundary goes.
 */
export function CompanyStoryIntro({ titleId, eyebrow, title, lede, cta, children }: Props) {
  return (
    <Stack spacing={5}>
      <Typography variant="overline" sx={{ color: 'primary.main' }}>
        {eyebrow}
      </Typography>

      <Typography
        id={titleId}
        variant="h2"
        component="h2"
        sx={{ fontSize: TITLE_SIZE, lineHeight: { xs: 1.25, md: 1.08 } }}
      >
        {title}
      </Typography>

      <Typography variant="subtitle1" color="text.secondary">
        {lede}
      </Typography>

      {children}

      {cta && (
        <AppButton to={cta.to} appVariant="secondary" sx={{ alignSelf: 'flex-start' }}>
          {cta.label}
        </AppButton>
      )}
    </Stack>
  );
}
